import _ from 'lodash';
import { AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import ApiService from '../../services/ApiService';
import PaymentSaleForm from './PaymentSaleForm';
import {
  PaymentSession,
  PaymentSessionCancelResponse,
  PaymentSessionSuccessResponse,
  Invoice,
  PaymentDepartment,
} from './types';
import LoadingPage from '../LoadingPage';
import FlexAlert from '../FlexAlert';

interface Props {
  action?: 'sale' | 'credit';
  amount?: number;
  customerNum?: string;
  department?: PaymentDepartment;
  enabledHostedPayments?: boolean;
  invoiceNum?: string;
  invoices?: Invoice[];
  noCash?: boolean;
  onCancel: (response: PaymentSessionCancelResponse) => void;
  onSuccess: (response: PaymentSessionSuccessResponse) => void;
  openOnly?: boolean;
  readOnly?: boolean;
  sessionId?: string;
  userInitials: string;
}

/**
 * This can resume using a sessionId (preferred) or invoiceNum
 */
const PaymentSaleFormResume = ({
  action = 'sale',
  amount,
  customerNum,
  department,
  enabledHostedPayments = true,
  invoiceNum,
  invoices,
  noCash = false,
  onCancel,
  onSuccess,
  openOnly = false,
  readOnly = false,
  sessionId,
  userInitials,
}: Props) => {
  const [paymentSession, setPaymentSession] = useState<PaymentSession | null>();

  useEffect(() => {
    const params: any = { sessionId, invoiceNum, invoices };
    if (openOnly) {
      params.status = 'open';
    }

    ApiService.get('/payment/session', {
      params,
    }).then((response: AxiosResponse) => {
      const sessions = response.data;

      if (sessions && sessions.length) {
        setPaymentSession(sessions[0]);
      } else {
        setPaymentSession(null);
      }
    });
  }, []);

  const getTerminalId = () => {
    if (!paymentSession || !paymentSession.CardTransactions || !paymentSession.CardTransactions.length) {
      return;
    }

    return paymentSession.CardTransactions[0].terminalId;
  };

  if (paymentSession === undefined) {
    // still loading
    return <LoadingPage />;
  }

  if (!paymentSession) {
    // no payment session found, start a new one

    if (!amount || !customerNum || !department || (!invoiceNum && !invoices)) {
      // not enough props were passed to start a payment
      return <FlexAlert modalClose>Could not resume a payment session with given parameters.</FlexAlert>;
    }

    const invoiceProps: any = {};
    if (invoiceNum) {
      invoiceProps.invoiceNum = invoiceNum;
    } else if (invoices) {
      invoiceProps.invoices = invoices;
    }

    return (
      <PaymentSaleForm
        action={action}
        amount={amount}
        customerNum={customerNum}
        department={department}
        enabledHostedPayments={enabledHostedPayments}
        invoiceNum={invoiceNum}
        invoices={invoices}
        noCash={noCash}
        onCancel={onCancel}
        onSuccess={onSuccess}
        readOnly={readOnly}
        selectCashInvoicePrinter={department === 'parts'}
        userInitials={userInitials}
        {...invoiceProps}
      />
    );
  }

  return (
    <PaymentSaleForm
      action={action}
      amount={paymentSession.amount}
      cardTransactions={paymentSession.CardTransactions}
      cashTransactions={paymentSession.CashTransactions}
      customerNum={paymentSession.customerNum}
      department={paymentSession.department}
      enabledHostedPayments={enabledHostedPayments}
      invoices={paymentSession.invoices}
      noCash={noCash}
      onCancel={onCancel}
      onSuccess={onSuccess}
      readOnly={readOnly}
      selectCashInvoicePrinter={department === 'parts'}
      sessionId={paymentSession.id}
      terminalId={getTerminalId()}
      userInitials={userInitials}
    />
  );
};

export default PaymentSaleFormResume;
